<template>
    <div class="cart-line-fields-tab modl-tab-body">
        <div class="modl-body">
            <!-- Item options -->
            <OptionSets v-if="optionSetsWithOptions.length"
                        v-model="itemOptionSelections"
                        :option-sets="optionSetsWithOptions" />

            <!-- Ask-early custom fields -->
            <form v-if="fieldInstances.length"
                  id="custom-fields-form"
                  novalidate
                  class="form-v form--shade"
                  :class="[{'mt-1': optionSets.length > 1}]"
                  @submit.prevent="submit">
                <FieldInstancesForm mode="edit"
                                    :instances="fieldInstances" />

                <!-- Having a submit button makes it so you can press enter to submit the form. -->
                <input type="submit"
                       class="hidden" />
            </form>

            <!-- If there are no item options and no ask-early fields, show a message to that effect. -->
            <template v-if="!optionSets.length && !fieldInstances.length">
                <div class="alert alert-info">
                    {{ l('ITEM_EDIT.NO_OPTIONS') }}
                </div>
            </template>
        </div>
    </div>

    <div class="modl-footer">
        <!-- Cancel button -->
        <SquareBtn size="lg"
                   class="cancel-btn"
                   @click="$emit('cancel')">
            {{ l(cancelLabel) }}
        </SquareBtn>

        <!-- OK button -->
        <SquareBtn color="primary"
                   size="lg"
                   @click="submit">
            {{ l('SAVE') }}
        </SquareBtn>
    </div>
</template>

<script>
import FieldInstanceFilter from '@/lib/CustomFields/FieldInstanceFilter';
import FieldInstancesForm from '@/vue/components/CustomFields/FieldInstancesForm';
import OptionSets from '@/epos/vue/modals/CartLineDetails/OptionSets';
import SquareBtn from '@/vue/components/SquareBtn';
import { CartLineDetailsTaxMixin } from '@/epos/vue/modals/CartLineDetails/CartLineDetailsTaxMixin';
import { cleanCartLine } from '@/libraries/DigiTickets/Cart/cartLineCleaning';
import { cloneDeep } from '@/functions/clone';
import { ConfirmTabAwayMixin } from '@/vue/mixins/ConfirmTabAwayMixin';
import { container } from '@/container';
import { extractFieldValuesFromForm } from '@/lib/CustomFields/fieldForm';
import { setValuesOfFieldGroup } from '@/lib/CustomFields/fieldFunctions';
import { sortByNavOrder } from '@/functions/arraySorting';
import { validateItemOptionSelections } from '@/lib/ItemOptions/itemOptionValidation';

export default {
    name: 'CartLineFieldsTab',

    components: {
        OptionSets,
        SquareBtn,
        FieldInstancesForm,
    },

    mixins: [
        CartLineDetailsTaxMixin,
        ConfirmTabAwayMixin,
    ],

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            /**
             * The FieldGroup (just one) to display in the tab.
             * This will contain all the ask early fields for the cart line. For ask early fields (item options)
             * we only set them on the first instance and then copy them to other instances on the line, because
             * they will be the same for all instances on the line.
             *
             * @type {FieldGroup}
             */
            fieldGroup: null,

            /**
             * Upon loading, store what the existing values of the fields were.
             * This will be compared against later to see if there are any changes made and we can alert
             * the user when they navigate away from the tab if there are unsaved changes.
             */
            initialValues: {},

            /**
             * @type {OptionSet[]}
             */
            optionSets: [],

            /**
             * @returns {Object<number, OptionSetSelections>}
             */
            itemOptionSelections: {},
        };
    },

    computed: {
        fieldInstances() {
            if (this.fieldGroup) {
                const instances = this.fieldGroup.instances.filter(FieldInstanceFilter.ASK_EARLY_FIELDS_ONLY);
                sortByNavOrder(instances);
                return instances;
            }

            return [];
        },

        /**
         * @returns {OptionSet[]}
         */
        optionSetsWithOptions() {
            return this.optionSets.filter((optionSet) => optionSet.options.length > 0);
        },
    },

    async created() {
        if (this.cartLine.itemInstances.length) {
            // Set the field group as a clone of the field group for the first item instance on the line.
            this.fieldGroup = cloneDeep(this.cartLine.itemInstances[0].fields);

            this.$nextTick(() => {
                this.initialValues = extractFieldValuesFromForm();
            });
        }

        // Load all the option sets applicable to the cart line.
        this.optionSets = await container.itemOptionManager.findOptionSetsForItem(this.cartLine.item?.ID);

        this.itemOptionSelections = this.cartLine.getItemOptionSelections();
    },

    methods: {
        async submit() {
            // Validate option sets.
            const itemOptionErrors = validateItemOptionSelections(
                this.optionSets,
                this.itemOptionSelections
            );
            if (itemOptionErrors.length > 0) {
                container.toastFactory.warningTop(
                    'ITEM_OPTIONS.ERROR_TITLE',
                    itemOptionErrors.join(' ')
                );
                return;
            }

            // Grab the field data from the form.
            const fieldValues = extractFieldValuesFromForm();

            // Set the values on the first item instance on the cart line.
            // Here we only set the "ask early" field values because these are the only ones displayed by the modal.
            if (this.cartLine.itemInstances.length) {
                setValuesOfFieldGroup(
                    fieldValues,
                    this.cartLine.itemInstances[0].fields,
                    FieldInstanceFilter.ASK_EARLY_FIELDS_ONLY,
                    true,
                );
            }

            // Call the cartLineCleaner which will copy those askEarly values to all the instances on the line.
            cleanCartLine(this.cartLine);

            await container.itemOptionManager.setItemOptionSelectionsForLine(
                container.cartService.currentCart,
                this.cartLine,
                this.itemOptionSelections
            );
            await container.cartService.recalculateAfterModifyingCartContents();

            this.$emit('resolve');
        },

        hasUnsavedChanges() {
            return JSON.stringify(this.initialValues) !== JSON.stringify(
                extractFieldValuesFromForm()
            );
        },
    },
};
</script>
